import { useMemo } from 'react';
import { validateDocument } from '../components/FormSubmissionFields';

interface FormField {
  itemType: 'question' | 'warning' | 'instruction' | 'note' | 'document';
  name?: string;
  label: string;
  fieldType?: string;
  options?: string[];
  placeholder?: string;
  validation?: Record<string, any>;
  dependency?: Record<string, any>;
  documentType?: string; 
  allowMultiple?: boolean;
}

interface FormSection {
  heading: string;
  description?: string;
  items: FormField[];
}

interface FormProgress {
  overallCompletion: number; // 0-100
  sectionProgress: {
    sectionIndex: number;
    heading: string;
    completion: number; // 0-100
  }[];
}

/**
 * A minimal replica of the dependency check logic from SubmissionForm.
 * You can refactor to a shared utility if needed to ensure total consistency.
 */
function checkSingleDependency(
  watchValues: Record<string, any>,
  dependency: { field: string; value: string }
): boolean {
  const currentValue = watchValues[dependency.field];
  
  // If the current value is an array (like from checkboxes),
  // check if it includes the dependency value
  if (Array.isArray(currentValue)) {
    return currentValue.includes(dependency.value);
  }
  
  // Otherwise do the regular equality check
  return currentValue === dependency.value;
}

function shouldDisplayField(
  watchValues: Record<string, any>,
  dependency?: Record<string, any>
): boolean {
  if (!dependency) return true;
  if (dependency.field && dependency.value) {
    return checkSingleDependency(watchValues, dependency as { field: string; value: string });
  }
  if (dependency.not) {
    const isSatisfied = checkSingleDependency(watchValues, dependency.not);
    return !isSatisfied;
  }
  if (dependency.all && Array.isArray(dependency.all)) {
    return dependency.all.every((cond: { field: string; value: string }) =>
      checkSingleDependency(watchValues, cond)
    );
  }
  return true;
}

/**
 * useFormProgress:
 *  - Reads formSections, formData
 *  - Finds all *required & visible* fields
 *  - Checks if they are complete
 *  - Returns per-section & overall completion as 0-100
 */
export function useFormProgress(
  formSections: FormSection[],
  formData: Record<string, any>
): FormProgress {
  return useMemo(() => {
    let totalRequired = 0;
    let totalCompleted = 0;

    const sectionProgress = formSections.map((section, sectionIndex) => {
      let sectionRequired = 0;
      let sectionCompleted = 0;

      for (const item of section.items) {
        // Only track "question" items that are required and visible
        if ((item.itemType === 'question') && item.validation?.required) {
          const visible = shouldDisplayField(formData, item.dependency);
          if (!visible) continue;

          sectionRequired += 1;
          totalRequired += 1;

          const value = item.name ? formData[item.name] : undefined;
          
          // Enhanced completion check based on field type
          let isComplete = false;
          
          // Handle null or undefined values
          if (value === null || value === undefined) {
            isComplete = false;
          }
          else if (item.fieldType) {
            switch (item.fieldType) {
              case 'text':
                isComplete = typeof value === 'string' && value.trim() !== '';
                break;
              case 'radio':
                isComplete = value !== '';
                break;
              case 'dropdown-single':
                isComplete = value !== '';
                break;
              case 'dropdown-multiple':
                isComplete = Array.isArray(value) && value.length > 0;
                break;
              case 'checkboxes':
                if (Array.isArray(value)) {
                  isComplete = value.length > 0;
                } else if (typeof value === 'object') {
                  isComplete = Object.values(value).some(v => Boolean(v));
                } else {
                  isComplete = Boolean(value);
                }
                break;
              default:
                isComplete = Boolean(value);
            }
          }
          // Fallback for other item types
          else {
            isComplete = Boolean(value);
          }
          
          if (isComplete) {
            sectionCompleted += 1;
            totalCompleted += 1;
          }
        }
        else if (item.itemType === 'document') {
          const visible = shouldDisplayField(formData, item.dependency);
          if (!visible) continue;

          sectionRequired += 1;
          totalRequired += 1;

          const documentValue = item.name ? formData[item.name] : undefined;
          
          if (validateDocument(documentValue)) {
            sectionCompleted += 1;
            totalCompleted += 1;
          }
        }
      }

      const sectionCompletion =
        sectionRequired > 0 ? Math.round((sectionCompleted / sectionRequired) * 100) : 100;
      return {
        sectionIndex,
        heading: section.heading || `Section ${sectionIndex + 1}`,
        completion: sectionCompletion,
      };
    });

    const overallCompletion =
      totalRequired > 0 ? Math.round((totalCompleted / totalRequired) * 100) : 100;

    return {
      overallCompletion,
      sectionProgress,
    };
  }, [formSections, formData]);
} 